/**
 * JobDependencyGraph — drag-and-drop dependency editor for "Custom Order"
 * run mode.
 *
 * Replaces the old linear up/down reorder list: jobs are rendered as nodes
 * on a canvas and the user connects them by dragging from one job's right
 * ("finishes first") handle to another job's left ("runs after") handle.
 * One job can have many dependents (one-to-many) and depend on many jobs
 * itself (many-to-one) — this is a general dependency graph, not a single
 * ordered list.
 *
 * Persists via PEJob.dependsOn (list of prerequisite job IDs) through the
 * existing PUT /jobs/{id} endpoint (updateJob). Node positions are laid
 * out automatically (topological levels, left-to-right) and are not
 * persisted — there is no backend column for canvas position, only for the
 * dependency graph itself.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
  type OnConnect,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Loader2 } from "lucide-react";
import type { PEJob } from "../../features/pipeline-engineer/types";
import { updateJob } from "../../features/pipeline-engineer/api/pe-client";

interface JobDependencyGraphProps {
  pipelineId: string;
  jobs: PEJob[];
  /** Called after a dependency edge is successfully added or removed, with
   *  the affected job's id and its new `dependsOn` list, so the parent can
   *  patch local state directly (no full job-list refetch, which would
   *  otherwise unmount this graph behind a loading spinner mid-drag). */
  onDependsOnChange: (jobId: string, dependsOn: string[]) => void;
}

interface JobNodeData extends Record<string, unknown> {
  label: string;
  targetTable: string;
  targetLayer: string;
}

const NODE_WIDTH = 200;
const NODE_HEIGHT = 64;
const LEVEL_GAP_X = 260;
const ROW_GAP_Y = 90;

/**
 * Assign each job a topological "level" (0 = no dependencies) by following
 * dependsOn edges. Jobs involved in a cycle (no reachable zero-indegree
 * root) are placed at level 0 as a fallback so they still render.
 */
function computeLevels(jobs: PEJob[]): Map<string, number> {
  const byId = new Map(jobs.map((j) => [j.id, j]));
  const levels = new Map<string, number>();
  const visiting = new Set<string>();

  function levelOf(jobId: string): number {
    const cached = levels.get(jobId);
    if (cached !== undefined) return cached;
    if (visiting.has(jobId)) return 0; // cycle guard
    visiting.add(jobId);
    const job = byId.get(jobId);
    const deps = (job?.dependsOn ?? []).filter((depId) => byId.has(depId));
    const level = deps.length === 0 ? 0 : Math.max(...deps.map(levelOf)) + 1;
    visiting.delete(jobId);
    levels.set(jobId, level);
    return level;
  }

  for (const job of jobs) levelOf(job.id);
  return levels;
}

function buildNodes(jobs: PEJob[]): Node<JobNodeData>[] {
  const levels = computeLevels(jobs);
  const countPerLevel = new Map<number, number>();

  return jobs.map((job) => {
    const level = levels.get(job.id) ?? 0;
    const row = countPerLevel.get(level) ?? 0;
    countPerLevel.set(level, row + 1);
    return {
      id: job.id,
      type: "job",
      position: { x: level * LEVEL_GAP_X, y: row * ROW_GAP_Y },
      data: {
        label: job.jobName,
        targetTable: job.targetTable,
        targetLayer: job.targetLayer,
      },
    };
  });
}

function buildEdges(jobs: PEJob[]): Edge[] {
  const byId = new Set(jobs.map((j) => j.id));
  const edges: Edge[] = [];
  for (const job of jobs) {
    for (const depId of job.dependsOn ?? []) {
      if (!byId.has(depId)) continue; // dangling dependency — ignore in the graph
      edges.push({
        id: `${depId}->${job.id}`,
        source: depId,
        target: job.id,
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { stroke: "rgba(255,255,255,0.35)" },
      });
    }
  }
  return edges;
}

/** Transitive dependency closure of `jobId` (jobs it must wait for, directly or indirectly). */
function ancestorsOf(jobId: string, byId: Map<string, PEJob>): Set<string> {
  const seen = new Set<string>();
  const stack = [...(byId.get(jobId)?.dependsOn ?? [])];
  while (stack.length > 0) {
    const next = stack.pop();
    if (next === undefined || seen.has(next)) continue;
    seen.add(next);
    stack.push(...(byId.get(next)?.dependsOn ?? []));
  }
  return seen;
}

function JobNode({ data }: NodeProps<Node<JobNodeData>>) {
  return (
    <div
      style={{ width: NODE_WIDTH, minHeight: NODE_HEIGHT }}
      className="rounded-lg border border-white/15 bg-[#14151c] px-3 py-2 shadow-lg"
    >
      <Handle type="target" position={Position.Left} />
      <div className="text-xs font-medium text-white truncate">
        {data.label}
      </div>
      <div className="text-[11px] text-white/40 truncate">
        {data.targetLayer} · {data.targetTable}
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

const NODE_TYPES = { job: JobNode };

export function JobDependencyGraph({
  pipelineId,
  jobs,
  onDependsOnChange,
}: JobDependencyGraphProps) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<JobNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const byId = useMemo(() => new Map(jobs.map((j) => [j.id, j])), [jobs]);

  // Re-derive the canvas from the authoritative `jobs` prop whenever it
  // changes (job added/removed, or a dependency saved elsewhere). This is
  // the single source of truth for nodes/edges — `onConnect`/`onEdgesDelete`
  // below never touch `setNodes`/`setEdges` directly, they just persist the
  // change and let the parent's updated `jobs` prop flow back through here.
  useEffect(() => {
    setNodes(buildNodes(jobs));
    setEdges(buildEdges(jobs));
  }, [jobs, setNodes, setEdges]);

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (pending) return;
      const { source, target } = connection;
      if (!source || !target || source === target) return;

      const sourceJob = byId.get(source);
      const targetJob = byId.get(target);
      if (!sourceJob || !targetJob) return;

      const existingDeps = targetJob.dependsOn ?? [];
      if (existingDeps.includes(source)) return; // already connected

      // Reject if this would create a cycle: source must not already
      // (transitively) depend on target.
      if (ancestorsOf(source, byId).has(target)) {
        setError(
          `Cannot connect "${sourceJob.jobName}" \u2192 "${targetJob.jobName}" \u2014 that would create a circular dependency.`,
        );
        return;
      }

      setError(null);
      setPending(true);
      const newDeps = [...existingDeps, source];
      updateJob(pipelineId, target, { depends_on: newDeps })
        .then(() => onDependsOnChange(target, newDeps))
        .catch((err: unknown) => {
          setError(
            err instanceof Error ? err.message : "Failed to save dependency.",
          );
        })
        .finally(() => setPending(false));
    },
    [byId, pending, pipelineId, onDependsOnChange],
  );

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      if (pending) return;
      const updates = new Map<string, Set<string>>();
      for (const edge of deleted) {
        const targetJob = byId.get(edge.target);
        if (!targetJob) continue;
        const dependencies =
          updates.get(edge.target) ?? new Set(targetJob.dependsOn ?? []);
        dependencies.delete(edge.source);
        updates.set(edge.target, dependencies);
      }
      if (updates.size === 0) return;

      setError(null);
      setPending(true);
      void (async () => {
        try {
          for (const [target, dependencies] of updates) {
            const newDeps = [...dependencies];
            await updateJob(pipelineId, target, { depends_on: newDeps });
            onDependsOnChange(target, newDeps);
          }
        } catch (err: unknown) {
          setError(
            err instanceof Error ? err.message : "Failed to remove dependency.",
          );
        } finally {
          setPending(false);
        }
      })();
    },
    [byId, pending, pipelineId, onDependsOnChange],
  );

  return (
    <div className="space-y-2">
      <p className="text-white/40 text-xs px-1">
        Drag from the right edge of a job to the left edge of another to set a
        dependency — the job you drag from runs first. Select a connection and
        press Delete/Backspace to remove it.
      </p>
      {error !== null && (
        <p className="text-red-400 text-xs px-1" role="alert">
          {error}
        </p>
      )}
      <div
        style={{ height: Math.max(280, jobs.length * 50) }}
        className="rounded-lg border border-white/10 bg-black/20 relative"
        // Defensive guard: if a stray native HTML5 drag/drop ever reaches
        // this container (e.g. from an ancestor with draggable content),
        // stop the browser's default "navigate to dropped content"
        // behavior rather than letting it blow away the SPA.
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      >
        {pending && (
          <div className="absolute top-2 right-2 z-10">
            <Loader2 size={14} className="animate-spin text-white/40" />
          </div>
        )}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgesDelete={onEdgesDelete}
          nodeTypes={NODE_TYPES}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={16} color="rgba(255,255,255,0.06)" />
          <Controls showInteractive={false} />
          <MiniMap
            pannable
            zoomable
            maskColor="rgba(0,0,0,0.6)"
            style={{ backgroundColor: "#14151c" }}
          />
        </ReactFlow>
      </div>
    </div>
  );
}
